import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangle, Boxes, HelpCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { useAuth } from '../../lib/authContext';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd } from '../../lib/format';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';

/**
 * SIL-271 (W4-T11, resources/plans/erp-register-import-spec.md §1 decisions
 * 2/3/9 + §2 prototype learnings #2/#3/#6, §3 Wave 3 SIL-268) — the ERP
 * register import triage queue.
 *
 * Consumes `GET /api/client/erp-register/batches/:batchId/triage` (SIL-268),
 * which lazily re-runs auto-rematch before returning the queue, and the five
 * triage action endpoints (`POST /rows/:stagingId/triage/{dismiss|
 * keep-existing|keep-erp|accept-even-split|accept-residual}`).
 *
 * RBAC mirrors the server (spec decision 7 — org admin only for triage
 * actions; members read-only): action buttons render only for `isAdmin`;
 * the server is the real 403 boundary (matches routes/financials/policy.tsx's
 * pattern) — this screen just hides controls a member can't use.
 *
 * Reached from the SIL-270 mapper wizard's match-summary step (batchId in
 * the URL) or from re-visiting an in-progress import.
 */
export const Route = createFileRoute('/financials/triage/$batchId')({
  component: TriageQueuePage,
});

// ---------------------------------------------------------------------------
// Wire shapes — NOT Zod-validated on the server (erp-register.ts builds this
// JSON ad hoc, not via a shared DTO schema), so these are hand-mirrored types
// matching the route's actual output rather than an import from
// @siliconx/shared. Keep in sync with apps/api/src/routes/client/
// erp-register.ts's `GET /batches/:batchId/triage` handler.
// ---------------------------------------------------------------------------

interface TriageFieldSet {
  purchasePriceUsd: number | null;
  purchasedAt: string | null;
  poRef: string | null;
  erpReportedNbv: number | null;
  erpNbvAsOf: string | null;
}

interface TriageItemBase {
  stagingId: string;
  rowNum: number;
  candidateAssetIds: string[];
}

interface ConflictItem extends TriageItemBase {
  kind: 'conflict';
  availableActions: ['keep_erp', 'keep_existing'];
  existing: TriageFieldSet | null;
  incoming: TriageFieldSet;
}

interface AmbiguousItem extends TriageItemBase {
  kind: 'ambiguous_match';
  availableActions: ['dismiss'];
}

interface HeterogeneousLumpItem extends TriageItemBase {
  kind: 'heterogeneous_lump';
  availableActions: ['dismiss'];
}

interface LumpPartialItem extends TriageItemBase {
  kind: 'lump_partial';
  availableActions: ['accept_even_split', 'accept_residual', 'dismiss'];
  proposedEvenSplit: { costUsd: (number | null)[]; erpReportedNbvUsd: (number | null)[] };
  proposedResidual: { declaredQty: number; perUnitCostUsd: number | null; perUnitNbvUsd: number | null };
}

type TriageItem = ConflictItem | AmbiguousItem | HeterogeneousLumpItem | LumpPartialItem;

interface TriageQueueResponse {
  rematched: number;
  items: TriageItem[];
  // SIL-279: count of cleanly-matched, not-yet-applied rows — these never
  // appear in `items` (only ambiguous/lump_partial/pending/conflict get a
  // triage row), so a fully-matched batch would otherwise show nothing here
  // and have no way to write its data — the batch just sat stranded.
  unpromotedMatchedCount: number;
}

interface PromoteResponse {
  promoted: number;
  conflicts: unknown[];
  errors: unknown[];
  batchFinalized: boolean;
}

// ---------------------------------------------------------------------------
// Field diff row helper
// ---------------------------------------------------------------------------

function fieldRows(existing: TriageFieldSet | null, incoming: TriageFieldSet): Array<[string, string, string]> {
  const e = existing ?? { purchasePriceUsd: null, purchasedAt: null, poRef: null, erpReportedNbv: null, erpNbvAsOf: null };
  const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—');
  return [
    ['Purchase price', e.purchasePriceUsd !== null ? formatUsd(e.purchasePriceUsd) : '—', incoming.purchasePriceUsd !== null ? formatUsd(incoming.purchasePriceUsd) : '—'],
    ['Purchased at', fmtDate(e.purchasedAt), fmtDate(incoming.purchasedAt)],
    ['PO ref', e.poRef ?? '—', incoming.poRef ?? '—'],
    ['ERP-reported NBV', e.erpReportedNbv !== null ? formatUsd(e.erpReportedNbv) : '—', incoming.erpReportedNbv !== null ? formatUsd(incoming.erpReportedNbv) : '—'],
    ['NBV as of', fmtDate(e.erpNbvAsOf), fmtDate(incoming.erpNbvAsOf)],
  ];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function TriageQueuePage() {
  const { batchId } = Route.useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState<TriageQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState<PromoteResponse | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    (async () => {
      const res = await api.api.client['erp-register'].batches[':batchId'].triage.$get({
        param: { batchId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as TriageQueueResponse;
    })()
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(stagingId: string, action: string) {
    setActingId(stagingId);
    setActionError(null);
    try {
      const triageRows = api.api.client['erp-register'].rows[':stagingId'].triage;
      const param = { stagingId };
      const res =
        action === 'dismiss'
          ? await triageRows.dismiss.$post({ param })
          : action === 'keep-existing'
            ? await triageRows['keep-existing'].$post({ param })
            : action === 'keep-erp'
              ? await triageRows['keep-erp'].$post({ param })
              : action === 'accept-even-split'
                ? await triageRows['accept-even-split'].$post({ param })
                : await triageRows['accept-residual'].$post({ param });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      load();
    } catch (ex: unknown) {
      setActionError((ex as Error).message);
    } finally {
      setActingId(null);
    }
  }

  async function promoteMatched() {
    setPromoting(true);
    setActionError(null);
    try {
      const res = await api.api.client['erp-register'].batches[':batchId'].promote.$post({
        param: { batchId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as unknown as PromoteResponse;
      setPromoteResult(data);
      load();
    } catch (ex: unknown) {
      setActionError((ex as Error).message);
    } finally {
      setPromoting(false);
    }
  }

  const items = data?.items ?? [];
  const unpromotedMatchedCount = data?.unpromotedMatchedCount ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Import Triage" />
        <p className="mt-0.5 text-sm text-zinc-500">
          ERP register rows that need a human decision before they're written to book-value
          records.{' '}
          <Link to="/integrations" className="font-medium text-brand-600 hover:underline">
            Back to Integrations
          </Link>
        </p>
        {!isAdmin && (
          <p className="mt-1 text-xs text-amber-600">
            Read-only — only an org admin can resolve triage rows.
          </p>
        )}
      </div>

      {loading && <LoadingShell rows={4} />}
      {error && <div className="text-sm text-red-600">Error: {error}</div>}
      {actionError && <div className="text-sm text-red-600">Error: {actionError}</div>}

      {promoteResult?.batchFinalized && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <p className="flex items-center gap-2 text-sm font-medium text-emerald-800">
            <CheckCircle2 className="size-4" /> Import complete
          </p>
          <p className="mt-1 text-sm text-emerald-700">
            {promoteResult.promoted} row{promoteResult.promoted === 1 ? '' : 's'} promoted into your assets'
            financial data.{' '}
            <Link to="/financials" className="font-medium underline">
              Back to Financials
            </Link>
          </p>
        </div>
      )}

      {!loading && !error && unpromotedMatchedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <div>
            <p className="text-sm font-medium text-emerald-800">
              {unpromotedMatchedCount} row{unpromotedMatchedCount === 1 ? '' : 's'} matched cleanly and{' '}
              {unpromotedMatchedCount === 1 ? "hasn't" : "haven't"} been promoted yet.
            </p>
            <p className="mt-0.5 text-xs text-emerald-700">
              Promoting writes these rows' financial data to your assets.
              {promoteResult && !promoteResult.batchFinalized && (
                <>
                  {' '}
                  Promoted {promoteResult.promoted} so far
                  {promoteResult.conflicts.length > 0 && `, ${promoteResult.conflicts.length} conflict(s)`}
                  {promoteResult.errors.length > 0 && `, ${promoteResult.errors.length} error(s)`}.
                </>
              )}
            </p>
          </div>
          {isAdmin ? (
            <Button size="sm"
              disabled={promoting}
              onClick={promoteMatched}
            >
              {promoting ? 'Promoting…' : `Promote ${unpromotedMatchedCount} row${unpromotedMatchedCount === 1 ? '' : 's'}`}
            </Button>
          ) : (
            <p className="text-xs text-zinc-400">Only an org admin can promote rows.</p>
          )}
        </div>
      )}

      {!loading && !error && items.length === 0 && unpromotedMatchedCount === 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center shadow-sm">
          <CheckCircle2 className="mx-auto mb-3 size-8 text-emerald-400" />
          <p className="text-sm font-medium text-zinc-700">Nothing to triage</p>
          <p className="mt-1 text-sm text-zinc-500">
            Every row in this import either matched cleanly or has already been resolved.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <TriageRow
            key={item.stagingId}
            item={item}
            isAdmin={isAdmin}
            acting={actingId === item.stagingId}
            onAction={(action) => act(item.stagingId, action)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One triage row — kind-specific body + action buttons.
// ---------------------------------------------------------------------------

function TriageRow({
  item,
  isAdmin,
  acting,
  onAction,
}: {
  item: TriageItem;
  isAdmin: boolean;
  acting: boolean;
  onAction: (action: string) => void;
}) {
  return (
    <Card padding="md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <KindIcon kind={item.kind} />
          <div>
            <p className="text-sm font-medium text-zinc-800">
              Row {item.rowNum} — {KIND_LABEL[item.kind]}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">
              {item.candidateAssetIds.length} candidate asset{item.candidateAssetIds.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {item.availableActions.map((action) => (
              <ActionButton key={action} action={action} disabled={acting} onClick={() => onAction(toRoutePath(action))} />
            ))}
          </div>
        )}
      </div>

      {item.kind === 'conflict' && (
        <div className="mt-4 overflow-x-auto border-t border-zinc-100 pt-3">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">
                <th className="py-1.5 pr-4">Field</th>
                <th className="py-1.5 pr-4">Manual (existing)</th>
                <th className="py-1.5 pr-4">ERP (incoming)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {fieldRows(item.existing, item.incoming).map(([field, existingV, incomingV]) => (
                <tr key={field}>
                  <td className="py-2 pr-4 text-zinc-500">{field}</td>
                  <td className="py-2 pr-4 font-medium text-zinc-800">{existingV}</td>
                  <td className="py-2 pr-4 font-medium text-zinc-800">{incomingV}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {item.kind === 'lump_partial' && (
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-zinc-100 pt-3 sm:grid-cols-2">
          <div className="rounded-lg bg-zinc-50 p-3">
            <p className="text-xs font-semibold text-zinc-600">Even split (all candidates)</p>
            <p className="mt-1 text-sm text-zinc-700">
              {item.proposedEvenSplit.costUsd.map((v, i) => (
                <span key={i} className="mr-2 inline-block tabular-nums">
                  {v !== null ? formatUsd(v) : '—'}
                </span>
              ))}
            </p>
          </div>
          <div className="rounded-lg bg-zinc-50 p-3">
            <p className="text-xs font-semibold text-zinc-600">
              Residual (declared qty {item.proposedResidual.declaredQty})
            </p>
            <p className="mt-1 text-sm text-zinc-700">
              {item.proposedResidual.perUnitCostUsd !== null
                ? `${formatUsd(item.proposedResidual.perUnitCostUsd)} / unit`
                : '—'}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

const KIND_LABEL: Record<TriageItem['kind'], string> = {
  conflict: 'Manual value conflict',
  ambiguous_match: 'Ambiguous match',
  heterogeneous_lump: 'Mixed-SKU lump line',
  lump_partial: 'Partial lump allocation',
};

function KindIcon({ kind }: { kind: TriageItem['kind'] }) {
  const cls = 'mt-0.5 size-4 shrink-0';
  switch (kind) {
    case 'conflict':
      return <AlertTriangle className={cn(cls, 'text-red-500')} />;
    case 'ambiguous_match':
      return <HelpCircle className={cn(cls, 'text-amber-500')} />;
    case 'heterogeneous_lump':
    case 'lump_partial':
      return <Boxes className={cn(cls, 'text-brand-500')} />;
  }
}

function toRoutePath(action: string): string {
  return action.replace(/_/g, '-');
}

const ACTION_LABEL: Record<string, string> = {
  keep_erp: 'Keep ERP value',
  keep_existing: 'Keep existing (manual)',
  accept_even_split: 'Accept even split',
  accept_residual: 'Accept residual',
  dismiss: 'Dismiss — not an IT asset',
};

function ActionButton({
  action,
  disabled,
  onClick,
}: {
  action: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const destructive = action === 'dismiss' || action === 'keep_existing';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50',
        destructive
          ? 'border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50'
          : 'bg-brand-600 text-white hover:bg-brand-700',
      )}
    >
      {ACTION_LABEL[action] ?? action}
    </button>
  );
}
